'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon, Separator } from '@repo/ui';
import { ArrowUp, Check } from '@repo/ui/icons';
import { Popover } from 'radix-ui';
import { SortByTrigger } from './SortByTrigger';
export interface SortByOption<T> {
  value: T;
  label: string;
}
export type SortDirection = 'asc' | 'desc';
export interface SortByProps<T> {
  options: SortByOption<T>[];
  selectedOption: T | null;
  direction: SortDirection;
  onOptionChange: (value: T) => void;
  onDirectionChange: (direction: SortDirection) => void;
  onClear: () => void;
  clearable?: boolean;
  disabled?: boolean;
  width?: string;
  side?: 'bottom' | 'left' | 'right' | 'top';
  align?: 'start' | 'center' | 'end';
}
const sortByItemClasses = cn('text-2xs', 'leading-none', 'h-[30px]', 'shrink-0', 'px-[6px]', 'rounded-lg', 'flex', 'items-center', 'justify-between', 'gap-2xs', 'select-none', 'outline-none', 'cursor-pointer', 'text-foreground', 'hover:bg-background');
const sortByLabelClasses = cn('text-2xs', 'leading-none', 'p-xs', 'text-secondary-foreground', 'select-none');
export default function SortBy(t0) {
  const $ = _c(58);
  const {
    options,
    selectedOption,
    direction,
    onOptionChange,
    onDirectionChange,
    onClear,
    clearable: t1,
    disabled: t2,
    width: t3,
    side: t4,
    align: t5
  } = t0;
  const clearable = t1 === undefined ? true : t1;
  const disabled = t2 === undefined ? false : t2;
  const width = t3 === undefined ? "260px" : t3;
  const side = t4 === undefined ? "bottom" : t4;
  const align = t5 === undefined ? "start" : t5;
  let t6;
  if ($[0] !== options || $[1] !== selectedOption) {
    let t7;
    if ($[3] !== selectedOption) {
      t7 = opt => opt.value === selectedOption;
      $[3] = selectedOption;
      $[4] = t7;
    } else {
      t7 = $[4];
    }
    t6 = options.find(t7);
    $[0] = options;
    $[1] = selectedOption;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const selectedSortByOption = t6;
  let t7;
  if ($[5] !== direction || $[6] !== onDirectionChange) {
    t7 = () => onDirectionChange(direction === "asc" ? "desc" : "asc");
    $[5] = direction;
    $[6] = onDirectionChange;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== clearable || $[9] !== direction || $[10] !== disabled || $[11] !== onClear || $[12] !== selectedSortByOption || $[13] !== t7) {
    t8 = <Popover.Trigger asChild={true}><SortByTrigger selectedOption={selectedSortByOption} direction={direction} onClear={onClear} onDirectionToggle={t7} clearable={clearable} disabled={disabled} /></Popover.Trigger>;
    $[8] = clearable;
    $[9] = direction;
    $[10] = disabled;
    $[11] = onClear;
    $[12] = selectedSortByOption;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== width) {
    t9 = {
      width
    };
    $[15] = width;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("flex flex-col", "bg-popover overflow-hidden rounded-lg", "shadow-lg", "z-50", "border-[0.5px] border-border", "data-[state=open]:animate-slide-down-and-fade", "max-sm:w-[var(--radix-popover-content-available-width)]");
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className={sortByLabelClasses}>Order</span>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== onDirectionChange) {
    t12 = () => onDirectionChange("desc");
    $[19] = onDirectionChange;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="gap-2xs flex items-center"><Icon icon={<ArrowUp />} className="text-secondary-foreground rotate-180" /><span>Descending</span></span>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== direction) {
    t14 = direction === "desc" && <Icon icon={<Check />} className="text-secondary-foreground" />;
    $[22] = direction;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12 || $[25] !== t14) {
    t15 = <button type="button" className={sortByItemClasses} data-testid="sort-by-descending" onClick={t12}>{t13}{t14}</button>;
    $[24] = t12;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== onDirectionChange) {
    t16 = () => onDirectionChange("asc");
    $[27] = onDirectionChange;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="gap-2xs flex items-center"><Icon icon={<ArrowUp />} className="text-secondary-foreground" /><span>Ascending</span></span>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== direction) {
    t18 = direction === "asc" && <Icon icon={<Check />} className="text-secondary-foreground" />;
    $[30] = direction;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t16 || $[33] !== t18) {
    t19 = <button type="button" className={sortByItemClasses} data-testid="sort-by-ascending" onClick={t16}>{t17}{t18}</button>;
    $[32] = t16;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t15 || $[36] !== t19) {
    t20 = <div className="p-2xs flex flex-col">{t11}{t15}{t19}</div>;
    $[35] = t15;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Separator />;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className={sortByLabelClasses}>Sort by</span>;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== onOptionChange || $[41] !== options || $[42] !== selectedOption) {
    let t24;
    if ($[44] !== onOptionChange || $[45] !== selectedOption) {
      t24 = option => <button type="button" key={String(option.value)} className={sortByItemClasses} data-testid={`sort-by-option-${String(option.value)}`} onClick={() => onOptionChange(option.value)}><span>{option.label}</span>{selectedOption === option.value && <Icon icon={<Check />} className="text-secondary-foreground" />}</button>;
      $[44] = onOptionChange;
      $[45] = selectedOption;
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    t23 = options.map(t24);
    $[40] = onOptionChange;
    $[41] = options;
    $[42] = selectedOption;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[47] !== t23) {
    t24 = <div className="p-2xs flex max-h-[300px] flex-col overflow-y-auto">{t22}{t23}</div>;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== align || $[50] !== side || $[51] !== t20 || $[52] !== t24 || $[53] !== t9) {
    t25 = <Popover.Portal><Popover.Content side={side} align={align} sideOffset={5} collisionPadding={15} style={t9} className={t10}>{t20}{t21}{t24}</Popover.Content></Popover.Portal>;
    $[49] = align;
    $[50] = side;
    $[51] = t20;
    $[52] = t24;
    $[53] = t9;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t25 || $[56] !== t8) {
    t26 = <Popover.Root modal={true}>{t8}{t25}</Popover.Root>;
    $[55] = t25;
    $[56] = t8;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  return t26;
}